import { TextDropdown } from 'braid-design-system';

import { formatRange } from '../utilities.ts';

const hour = 60 * 60 * 1000;

const timeRangeOption = {
  '60 minutes': hour,
  '4 hours': 4 * hour,
  '24 hours': hour * 24,
  '3 days': 3 * hour * 24,
  '7 days': 7 * hour * 24,
  '30 days': 30 * hour * 24,
  '90 days': 90 * hour * 24,
};

export const timeRangeOptions = Object.keys(
  timeRangeOption,
) as TimeRangeOption[];

export type TimeRangeOption = keyof typeof timeRangeOption;

export type Range =
  | {
      option: Exclude<TimeRangeOption, 'custom'>;
      bounds?: never;
    }
  | {
      option: 'custom';
      bounds: [Date, Date];
    };

export const rangeToBounds = (range: Range, now: Date): [Date, Date] => {
  if (range.option === 'custom') {
    return range.bounds;
  }

  const rangeMs = timeRangeOption[range.option];
  let nowRounded = now.getTime();

  if (rangeMs > 48 * hour) {
    // round time down to the nearest 15 minutes
    // to avoid frequent updates
    nowRounded -= nowRounded % (15 * 60 * 1000);
  }

  return [new Date(nowRounded - rangeMs), new Date(nowRounded)];
};

export const RangePicker = ({
  setRange,
  range,
}: {
  setRange: (range: Range) => void;
  range: Range;
}) => (
  <TextDropdown<TimeRangeOption | 'custom'>
    id="date-time-range"
    label="Date time range"
    onChange={(newRange) => {
      if (newRange === 'custom') {
        return;
      }

      setRange({
        option: newRange,
      });
    }}
    value={range.option}
    options={[...timeRangeOptions, 'custom'].flatMap((key) => {
      if (key === 'custom') {
        return range.option === 'custom'
          ? [
              {
                text: key === 'custom' ? formatRange(range.bounds) : key,
                value: key as TimeRangeOption,
              },
            ]
          : [];
      }

      return [
        {
          text: key,
          value: key as TimeRangeOption,
        },
      ];
    })}
  />
);
